'use client'

import { useCallback, useEffect } from 'react'
import { WebContainer, type FileSystemTree } from '@webcontainer/api'
import { useWebContainerStore } from '@/lib/webcontainer-store'
import { useFileStore, type FileNode } from '@/lib/file-store'

let webcontainerPromise: Promise<WebContainer> | null = null

const convertToFileSystemTree = (files: FileNode[]): FileSystemTree => {
  const tree: FileSystemTree = {}
  
  for (const node of files) {
    if (node.type === 'file') {
      tree[node.name] = {
        file: {
          contents: node.content || ''
        }
      }
    } else if (node.type === 'folder' && node.children) {
      tree[node.name] = {
        directory: convertToFileSystemTree(node.children)
      }
    }
  }
  
  return tree
}

export function useWebContainer() {
  const { files } = useFileStore()
  const {
    instance,
    isBooting,
    isReady,
    previewUrl,
    setInstance,
    setBooting,
    setReady,
    setPreviewUrl,
    addTerminalOutput,
    clearTerminalOutput,
    setCurrentProcess,
    setError
  } = useWebContainerStore()

  const boot = useCallback(async () => {
    if (instance || isBooting) return instance

    try {
      setBooting(true)
      setError(null)
      addTerminalOutput('[info] Booting WebContainer...')

      if (!webcontainerPromise) {
        webcontainerPromise = WebContainer.boot()
      }

      const container = await webcontainerPromise
      setInstance(container)
      addTerminalOutput('[success] WebContainer booted successfully!')

      container.on('server-ready', (port, url) => {
        addTerminalOutput(`[success] Server ready on port ${port}`)
        setPreviewUrl(url)
      })

      container.on('error', (error) => {
        addTerminalOutput(`[error] ${error.message}`)
        setError(error.message)
      })

      setBooting(false)
      return container
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to boot WebContainer'
      addTerminalOutput(`[error] ${message}`)
      setError(message)
      setBooting(false)
      return null
    }
  }, [instance, isBooting, setInstance, setBooting, setPreviewUrl, addTerminalOutput, setError])

  const mountFiles = useCallback(async (container: WebContainer, filesToMount: FileNode[]) => {
    try {
      addTerminalOutput('[info] Mounting files...')
      const tree = convertToFileSystemTree(filesToMount)
      await container.mount(tree)
      addTerminalOutput(`[success] Mounted ${filesToMount.length} items`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to mount files'
      addTerminalOutput(`[error] ${message}`)
    }
  }, [addTerminalOutput])

  const runCommand = useCallback(async (command: string, args: string[] = []) => {
    if (!instance) {
      addTerminalOutput('[error] WebContainer not initialized')
      return
    }

    try {
      addTerminalOutput(`$ ${command} ${args.join(' ')}`)
      const process = await instance.spawn(command, args)
      setCurrentProcess(process)

      process.output.pipeTo(
        new WritableStream({
          write(data) {
            addTerminalOutput(data)
          }
        })
      )

      const exitCode = await process.exit
      addTerminalOutput(`[info] Process exited with code ${exitCode}`)
      setCurrentProcess(null)
      return exitCode
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Command failed'
      addTerminalOutput(`[error] ${message}`)
      setCurrentProcess(null)
    }
  }, [instance, addTerminalOutput, setCurrentProcess])

  const installAndRun = useCallback(async () => {
    clearTerminalOutput()
    
    const container = instance || await boot()
    if (!container) return

    await mountFiles(container, files)

    // Check if package.json exists
    const hasPackageJson = files.some(f => f.name === 'package.json')
    
    if (hasPackageJson) {
      addTerminalOutput('[info] Installing dependencies...')
      const installExitCode = await runCommand('npm', ['install'])
      
      if (installExitCode === 0) {
        addTerminalOutput('[info] Starting development server...')
        await runCommand('npm', ['run', 'dev'])
      }
    } else {
      // For simple HTML projects, start a static server
      addTerminalOutput('[info] No package.json found, starting static server...')
      
      // Create a simple server script
      await container.fs.writeFile(
        'server.js',
        `
const http = require('http');
const fs = require('fs');
const path = require('path');

const server = http.createServer((req, res) => {
  let filePath = req.url === '/' ? '/index.html' : req.url;
  filePath = '.' + filePath;
  
  const extname = path.extname(filePath);
  const contentTypes = {
    '.html': 'text/html',
    '.css': 'text/css',
    '.js': 'application/javascript',
    '.json': 'application/json',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.svg': 'image/svg+xml'
  };
  
  fs.readFile(filePath, (err, content) => {
    if (err) {
      res.writeHead(404);
      res.end('File not found');
    } else {
      res.writeHead(200, { 'Content-Type': contentTypes[extname] || 'text/plain' });
      res.end(content);
    }
  });
});

server.listen(3000, () => console.log('Server ready on port 3000'));
`
      )
      await runCommand('node', ['server.js'])
    }
    
    setReady(true)
  }, [instance, boot, files, mountFiles, runCommand, clearTerminalOutput, addTerminalOutput, setReady])

  const writeFile = useCallback(async (path: string, content: string) => {
    if (!instance) return
    try {
      await instance.fs.writeFile(path, content)
    } catch (error) {
      console.error('Failed to write file:', error)
    }
  }, [instance])

  // Sync file changes to WebContainer
  useEffect(() => {
    if (!instance || !isReady) return

    const syncFiles = async () => {
      const tree = convertToFileSystemTree(files)
      try {
        await instance.mount(tree)
      } catch (error) {
        console.error('Failed to sync files:', error)
      }
    }

    const timeout = setTimeout(syncFiles, 500)
    return () => clearTimeout(timeout)
  }, [files, instance, isReady])

  return {
    boot,
    mountFiles,
    runCommand,
    installAndRun,
    writeFile,
    isBooting,
    isReady,
    previewUrl
  }
}
